import {
  enableFlagCleanupStatesFilter,
  flagListLimitGuardrail,
  getFlagListPageSize,
  isABTestingEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import invariant from 'tiny-invariant';

import { ApiVersions } from '../api-versions';
import type { GetFeatureFlagsOpenAPIQueryParams } from '../generated/operations/raw-openapi-query-params-for-serializers';

export interface GetFeatureFlagsQueryParams extends Omit<
  GetFeatureFlagsOpenAPIQueryParams,
  'env' | 'sort' | 'state' | 'filter' | 'expand'
> {
  env?: string[];
  sort?:
    | 'creationDate'
    | '-creationDate'
    | 'key'
    | '-key'
    | 'maintainerId'
    | '-maintainerId'
    | 'name'
    | '-name'
    | 'tags'
    | '-tags'
    | 'targetingModifiedDate'
    | '-targetingModifiedDate'
    | 'type'
    | '-type';
  filter?: {
    query?: string;
    type?: 'permanent' | 'temporary';
    status?: 'new' | 'inactive' | 'active' | 'launched';
    tags?: string[];
    hasExperiment?: boolean;
    hasDataExport?: boolean; // deprecated filter (v4 of the API)
    evaluated?: { after: number };
    maintainerId?: string;
    targeting?: 'on' | 'off';
    maintainerTeamKey?: string;
    contextKindTargeted?: string;
    contextKindsEvaluated?: string[];
    creationDate?: { before: number };
    segmentTargeted?: string;
    codeReferences?: { min?: number; max?: number };
    followerId?: string;
    excludeSettings?: Array<'prerequisites' | 'on' | 'targets' | 'rules' | 'fallthrough' | 'offVariation'>;
    sdkAvailability?: 'anyClient' | 'server' | 'mobile' | 'client';
    filterEnv?: string;
    view?: string;
    releasePipeline?: string;
    applicationEvaluated?: string;
    state?: 'live' | 'archived' | 'deprecated';
    staleStates?: 'readyForCodeRemoval' | 'readyToArchive';
    purpose?: 'holdout' | 'migration' | '!(holdout)' | '!(migration)' | 'all';
    guardedRollout?: 'any' | 'monitoring' | 'regressed' | 'rolledBack' | 'rolledBackAuto' | 'completed' | 'archived';
  };
  expand?: Array<'measuredRolloutStatus' | 'processes' | 'archiveChecks' | 'views'>;
}

function limitIsInRange(limit: number) {
  const clamped = Math.max(1, Math.min(limit || getFlagListPageSize(), getFlagListPageSize()));
  return clamped === limit;
}

export function querySerializer(params: GetFeatureFlagsQueryParams) {
  const searchParams = new URLSearchParams();

  const limit = params.limit;
  if (flagListLimitGuardrail()) {
    if (limit !== undefined) {
      invariant(limitIsInRange(limit), `Limit must be between 1 and ${getFlagListPageSize()}`);
      searchParams.set('limit', String(limit));
    }
  } else {
    if ((params.limit ?? 0) > 0) {
      searchParams.set('limit', String(params.limit));
    }
  }

  if (params.env) {
    for (const env of params.env) {
      searchParams.append('env', env);
    }
  }

  if ((params.offset ?? 0) > 0) {
    searchParams.set('offset', String(params.offset));
  }

  if (params.archived !== undefined) {
    searchParams.set('archived', String(params.archived));
  }

  if (params.compare !== undefined) {
    searchParams.set('compare', String(params.compare));
  }

  if (params.summary !== undefined) {
    searchParams.set('summary', String(params.summary));
  }

  if (params.sort) {
    // The targetingModifiedDate sorts must never be sent. Both this operation and
    // getFeatureFlags1 (/internal/flags/{projectKey}, which delegates to this serializer) pin
    // LD-API-Version 20240415 == internal version 4, and validateAPIV4Params rejects this sort
    // outright with a 400 ("targetingModifiedDate sort is not supported in this version").
    // Passing it through instead of dropping it fails the whole request, so callers that still
    // ask for it fall back to the API's default ordering rather than erroring.
    const isDeprecatedTargetingSort = ['targetingModifiedDate', '-targetingModifiedDate'].includes(params.sort);

    if (!isDeprecatedTargetingSort) {
      searchParams.set('sort', params.sort);
    }
  }

  if (params.filter) {
    const serializedFilter = serializeFlagListFilterParam(params.filter);
    if (serializedFilter) {
      searchParams.set('filter', serializedFilter);
    }
  }

  if (params?.expand) {
    searchParams.append('expand', params.expand.join(','));
  }

  return searchParams.toString();
}

export function serializeFlagListFilterParam(value: GetFeatureFlagsQueryParams['filter'] = {}) {
  const filter: string[] = [];

  if (value.query) {
    filter.push(`query:${encodeURIComponent(value.query)}`);
  }

  if (value.sdkAvailability) {
    filter.push(`sdkAvailability:${value.sdkAvailability}`);
  }

  if (value.filterEnv) {
    filter.push(`filterEnv:${value.filterEnv}`);
  }

  if (value.view) {
    filter.push(`view:${value.view}`);
  }

  if (value.maintainerId) {
    filter.push(`maintainerId:${value.maintainerId}`);
  }

  if (value.maintainerTeamKey) {
    filter.push(`maintainerTeamKey:${value.maintainerTeamKey}`);
  }

  if (value.evaluated) {
    filter.push(`evaluated:${JSON.stringify(value.evaluated)}`);
  }

  if (value.creationDate) {
    filter.push(`creationDate:${JSON.stringify(value.creationDate)}`);
  }

  if (value.tags && value.tags.length > 0) {
    filter.push(`tags:${value.tags.map(encodeURIComponent).join('+')}`);
  }

  if (value.type) {
    filter.push(`type:${value.type}`);
  }

  if (value.status) {
    filter.push(`status:${value.status}`);
  }

  if (value.segmentTargeted) {
    filter.push(`segmentTargeted:${value.segmentTargeted}`);
  }

  if (value.excludeSettings && value.excludeSettings.length > 0) {
    filter.push(`excludeSettings:${value.excludeSettings.join('+')}`);
  }

  if (value.releasePipeline) {
    filter.push(`releasePipeline:${value.releasePipeline}`);
  }

  if (value.applicationEvaluated) {
    filter.push(`applicationEvaluated:${value.applicationEvaluated}`);
  }

  if (value.state) {
    filter.push(`state:${value.state}`);
  }

  if (enableFlagCleanupStatesFilter() && value.staleStates) {
    filter.push(`staleState:${value.staleStates}`);
  }

  let purposeFilter = value.purpose ?? '';
  if (isABTestingEnabled()) {
    purposeFilter = purposeFilter ? `${purposeFilter}+!(holdout)` : '!(holdout)';
  }
  if (purposeFilter) {
    filter.push(`purpose:${purposeFilter}`);
  }

  if (value.guardedRollout !== undefined) {
    filter.push(`guardedRollout:${value.guardedRollout}`);
  }

  // aesthetics…
  filter.sort();

  return filter.join(',');
}

export const apiVersion = ApiVersions.V4;
